import i18n from '../i18n';
/**
 * R6.3 PHASE 2 — SOVERDI → Agri Safe dashboard adapter.
 *
 * Presentational mapping only. This module consumes the EXISTING Agri Safe truth
 * models and reshapes them into the SOVERDI view shapes. It never fetches, never
 * invents a value, and never weakens a truth rule — null/stale/unknown/uncommitted
 * all pass through intact. The truth rules themselves live in
 * `liveSensorMetrics` / `operationalOverview` / `sectorOperationalModel` /
 * `siteOperationalModel` and are not reimplemented here.
 *
 * Pure functions only — no React, no network, no side effects.
 */

import type { IoTAlert, IoTNode } from '../../lib/iotApi';
import type { OperationalState, SectorPlacement } from '../../lib/operationalOverview';
import { buildSectorModel, type SectorOperationalModel } from '../../lib/sectorOperationalModel';
import type { CgmTopology } from '../../config/cgmPilot';
import {
  canonicalDisplayName,
  normalizeCustomDisplayName,
  resolveSectorDisplayName,
  sectorNameKey,
  technicalFallbackName,
} from './sectorNaming';

/** SOVERDI irrigation status vocabulary, extended with a truthful UNKNOWN. */
export type SoverdiIrrigationStatus =
  | 'OFF'
  | 'SCHEDULED'
  | 'STARTING'
  | 'RUNNING'
  | 'STOPPING'
  | 'PAUSED'
  | 'FAULT'
  | 'OFFLINE'
  | 'UNKNOWN';

/** Agri Safe operational state → SOVERDI status, without inventing a state. */
export function mapOperationalState(state: OperationalState): SoverdiIrrigationStatus {
  switch (state) {
    case 'active':
      return 'RUNNING';
    case 'stopped':
      return 'OFF';
    case 'not_commissioned':
      return 'OFFLINE';
    case 'unknown':
    default:
      return 'UNKNOWN';
  }
}

/** Display metadata for the SOVERDI status badge, shared by both cards. */
export function getSoverdiStatusInfo(status: SoverdiIrrigationStatus): {
  label: string;
  color: string;
} {
  switch (status) {
    case 'RUNNING': return { label: i18n.t("sectorViews.running"), color: 'var(--color-success)' };
    case 'STARTING': return { label: i18n.t("sectorViews.Demarrage"), color: 'var(--color-info)' };
    case 'STOPPING': return { label: i18n.t("sectorViews.Arret"), color: 'var(--color-warning)' };
    case 'PAUSED': return { label: i18n.t("sectorViews.paused"), color: 'var(--color-warning)' };
    case 'SCHEDULED': return { label: i18n.t("sectorViews.Programme"), color: 'var(--color-info)' };
    case 'FAULT': return { label: i18n.t("sectorViews.Defaut"), color: 'var(--color-danger)' };
    case 'OFFLINE': return { label: i18n.t("nonCommissioned"), color: 'var(--gray-500)' };
    case 'UNKNOWN': return { label: i18n.t("sectorViews.Etat_inconnu"), color: 'var(--gray-500)' };
    case 'OFF':
    default: return { label: i18n.t("sectorViews.Arret_1"), color: 'var(--gray-500)' };
  }
}

export type MetricFreshness = 'fresh' | 'stale' | 'absent';

/** One sector in the SOVERDI view model. Null means "no real source" (render —). */
export interface SoverdiSectorView {
  /** Installed irrigation unit; technical sectorCode stays unchanged. */
  bindingId?: string;
  id: string;
  /**
   * Technical fallback label (`Roubia — A`). Retained as the defensive last
   * resort of the naming rule; `displayName` is what the farmer sees.
   */
  name: string;
  /** Farmer-facing name: custom ?? canonical ?? technical. Never empty. */
  displayName: string;
  /** Canonical parcel names of this sector, in topology order. */
  canonicalParcelNames: string[];
  /** Parcel-derived business name, or null when the topology declares none. */
  canonicalDisplayName: string | null;
  /** Operator override, when one is stored and valid. */
  customDisplayName: string | null;
  siteName: string;
  sectorCode: string;
  status: SoverdiIrrigationStatus;
  operationalState: OperationalState;

  /** Pressure in bar. `null` = absent (—); a real 0 stays 0. */
  pressureBar: number | null;
  pressureFreshness: MetricFreshness;
  /** Flow in m³/h, converted exactly from L/min. `null` = absent. */
  flowM3h: number | null;
  flowFreshness: MetricFreshness;
  /** Soil moisture %. `null` = absent. */
  soilMoisturePercent: number | null;
  soilMoistureFreshness: MetricFreshness;

  /** Valve state of the sector controller, truthfully UNKNOWN when unproven. */
  valveState: 'open' | 'closed' | 'unknown';

  /** Proven duration (ms) in the current state, or null when unproven. */
  stateSinceMs: number | null;

  /** These have no Agri Safe source; the view model keeps them null. */
  volumeM3: null;
  remainingMinutes: null;
  progressPercent: null;
}

/**
 * Exact L/min → m³/h conversion. 0 L/min stays 0 m³/h.
 *
 * Exported so the Sector Detail history charts convert flow through the SAME
 * function as the sector cards — two conversions could drift into two answers.
 */
export const lpmToM3h = (lpm: number): number => lpm * 0.06;

/** Naming inputs for a sector view. Both are optional and default to absent. */
export interface SectorNamingInput {
  /** Canonical parcel names from the topology, in declaration order. */
  parcelNames?: readonly string[];
  /** Operator override for this sector, when one is stored. */
  customDisplayName?: string | null;
}

/**
 * Map one built Agri Safe sector model into the SOVERDI sector view.
 *
 * Naming is layered on top of the operational model and never feeds back into
 * it: `id`, `siteName` and `sectorCode` are derived from topology identity
 * alone, so no display name can influence a join, a binding or a lookup.
 */
export function toSoverdiSectorView(
  model: SectorOperationalModel,
  siteName: string,
  sectorCode: string,
  naming: SectorNamingInput = {},
): SoverdiSectorView {
  const pressure = model.metrics.pressure;
  const flow = model.metrics.flow;
  const soilMoisture = model.metrics.soilMoisture;

  let valveState: SoverdiSectorView['valveState'] = 'unknown';
  if (model.controller) {
    // Reuse the same interpretation the sector model already derived: the
    // controller's fresh operational state is the only proof of valve position.
    if (model.operationalState === 'active') valveState = 'open';
    else if (model.operationalState === 'stopped') valveState = 'closed';
  }

  const parcelNames = [...(naming.parcelNames ?? [])];
  const canonical = canonicalDisplayName(parcelNames);
  const custom = normalizeCustomDisplayName(naming.customDisplayName);

  return {
    id: sectorNameKey(siteName, sectorCode),
    name: technicalFallbackName(siteName, sectorCode),
    displayName: resolveSectorDisplayName({
      customDisplayName: custom,
      canonicalDisplayName: canonical,
      siteName,
      technicalSectorCode: sectorCode,
    }),
    canonicalParcelNames: parcelNames,
    canonicalDisplayName: canonical,
    customDisplayName: custom,
    siteName,
    sectorCode,
    status: mapOperationalState(model.operationalState),
    operationalState: model.operationalState,

    pressureBar: pressure.value,
    pressureFreshness: pressure.freshness,
    flowM3h: flow.value === null ? null : lpmToM3h(flow.value),
    flowFreshness: flow.freshness,
    soilMoisturePercent: soilMoisture.value,
    soilMoistureFreshness: soilMoisture.freshness,

    valveState,
    stateSinceMs: model.stateSinceMs,

    volumeM3: null,
    remainingMinutes: null,
    progressPercent: null,
  };
}

export type SoverdiAlertSeverity = 'critical' | 'warning' | 'info';

export interface SoverdiAlert {
  id: string;
  severity: SoverdiAlertSeverity;
  message: string;
  source: string | null;
  timestamp: string;
  acknowledged: boolean;
}

const isValidSeverity = (severity: string): severity is SoverdiAlertSeverity =>
  severity === 'critical' || severity === 'warning' || severity === 'info';

/**
 * Map /iot/alerts rows into the SOVERDI alert shape. The backend has no
 * `acknowledged` flag — its `status` distinguishes open from acknowledged — so
 * a row is "acknowledged" only when its status says so; everything else stays
 * unacknowledged (the truthful default for a real alert).
 */
const DEFAULT_ALERT_SEVERITY = 'info' as const;

export function toSoverdiAlerts(
  alerts: IoTAlert[],
  unavailable: boolean,
): SoverdiAlert[] {
  void unavailable; // callers branch on `alertsUnavailable`, not on the list shape
  return alerts.map((alertRow) => ({
    id: alertRow.id,
    severity: isValidSeverity(alertRow.severity) ? alertRow.severity : DEFAULT_ALERT_SEVERITY,
    message: alertRow.message,
    source: alertRow.node_name ?? null,
    timestamp: alertRow.created_at,
    acknowledged: alertRow.status === 'acknowledged' || alertRow.status === 'acked',
  }));
}

export interface SoverdiKpiView {
  activeSectors: number;
  totalSectors: number;
  /** Water today has no real farm/day aggregate; always null (render —). */
  todayVolumeM3: null;
}

export interface SoverdiDashboardView {
  farmName: string;
  sectors: SoverdiSectorView[];
  kpis: SoverdiKpiView;
  alerts: SoverdiAlert[];
  /** true only when the /iot/alerts read FAILED — distinct from "no alerts". */
  alertsUnavailable: boolean;
  upcomingIrrigations: unknown[];
}

export interface BuildDashboardViewInput {
  farmName: string;
  sectors: SoverdiSectorView[];
  kpis: { activeSectors: number; totalSectors: number };
  alerts: SoverdiAlert[];
  alertsUnavailable: boolean;
  upcomingIrrigations: unknown[];
}

/**
 * Build the sector view list for EVERY (site, sector) of the farm topology.
 *
 * This is the deterministic composition point the hook uses: it consumes the
 * placement map `useFarmOperationalData` already resolved and reuses the shared
 * `buildSectorModel`, so the clone can never disagree with the main Agri Safe
 * Dashboard / Sector Detail about which nodes belong to a sector.
 */
export function buildSoverdiSectorViews(input: {
  topology: CgmTopology;
  nodes: IoTNode[];
  placement: Map<string, SectorPlacement>;
  now: number;
  /**
   * Operator overrides keyed by `site:code`. Absent or unreadable metadata
   * simply yields canonical names — it can never remove a sector.
   */
  customNames?: Map<string, string>;
}): SoverdiSectorView[] {
  const { topology, nodes, placement, now, customNames } = input;
  const views: SoverdiSectorView[] = [];
  for (const site of topology.sites) {
    for (const sector of site.sectors) {
      const model = buildSectorModel({
        nodes,
        placement,
        siteName: site.name,
        sectorCode: sector.code,
        now,
      });
      views.push(
        toSoverdiSectorView(model, site.name, sector.code, {
          parcelNames: sector.parcels.map((parcel) => parcel.name),
          customDisplayName: customNames?.get(sectorNameKey(site.name, sector.code)) ?? null,
        }),
      );
    }
  }
  return views;
}

/** Assemble the farm-level view. Schedule stays an explicit empty array. */
export function toSoverdiDashboardView(input: BuildDashboardViewInput): SoverdiDashboardView {
  return {
    farmName: input.farmName,
    sectors: input.sectors,
    kpis: { ...input.kpis, todayVolumeM3: null },
    alerts: input.alerts,
    alertsUnavailable: input.alertsUnavailable,
    upcomingIrrigations: input.upcomingIrrigations,
  };
}
